/**
 * Editable combobox: pick from a list of existing values or type a new one
 */

import { forwardRef, useState, useRef, useEffect, useImperativeHandle } from 'react';
import './Combobox.css';

/**
 * Text input with a filterable dropdown of suggestions
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {Array<string>} props.options - Existing values to suggest
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Called with the new raw string value
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.error - Error message
 * @param {boolean} props.required - Whether field is required
 * @returns {React.ReactElement} Combobox element
 */
const Combobox = forwardRef(function Combobox({
  label,
  options = [],
  placeholder = 'Select or type a value',
  error,
  required = false,
  className = '',
  id,
  name,
  value = '',
  onChange,
  onBlur,
  disabled,
  ...props
}, ref) {
  const comboId = id || `combobox-${label?.toLowerCase().replace(/\s+/g, '-')}`;
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => inputRef.current);

  const filteredOptions = value
    ? options.filter(opt => opt.toLowerCase().includes(value.toLowerCase()))
    : options;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const commitValue = (val) => {
    onChange?.(val);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        e.preventDefault();
        commitValue(filteredOptions[highlightedIndex]);
      } else {
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div
      className={`combobox-group ${error ? 'combobox-group--error' : ''} ${className}`}
      ref={wrapperRef}
    >
      {label && (
        <label htmlFor={comboId} className="combobox-group__label">
          {label}
          {required && <span className="combobox-group__required">*</span>}
        </label>
      )}
      <div className="combobox-group__wrapper">
        <input
          ref={inputRef}
          type="text"
          id={comboId}
          name={name}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={`${comboId}-listbox`}
          aria-invalid={error ? 'true' : 'false'}
          autoComplete="off"
          disabled={disabled}
          className="combobox-group__input"
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            onChange?.(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          {...props}
        />
        <button
          type="button"
          className="combobox-group__arrow-btn"
          tabIndex={-1}
          disabled={disabled}
          aria-label="Toggle suggestions"
          onClick={() => {
            setIsOpen(o => !o);
            inputRef.current?.focus();
          }}
        >
          <span className="combobox-group__arrow" />
        </button>
        {isOpen && filteredOptions.length > 0 && (
          <ul
            id={`${comboId}-listbox`}
            className="combobox-group__options"
            role="listbox"
          >
            {filteredOptions.map((opt, index) => (
              <li
                key={opt}
                role="option"
                aria-selected={opt === value}
                className={`combobox-group__option${index === highlightedIndex ? ' combobox-group__option--highlighted' : ''}${opt === value ? ' combobox-group__option--selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitValue(opt);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {opt}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && (
        <span className="combobox-group__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
});

export default Combobox;
